import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react'

export type ThemePalette = 'green' | 'blue' | 'red'
export type ThemeMode = 'light' | 'dark'
export type ThemeId =
  | 'green-light'
  | 'green-dark'
  | 'blue-light'
  | 'blue-dark'
  | 'red-light'
  | 'red-dark'

export const THEME_ORDER: ThemeId[] = [
  'green-light',
  'green-dark',
  'blue-light',
  'blue-dark',
  'red-light',
  'red-dark',
]

export const THEME_COLORS: Record<ThemeId, string> = {
  'green-light': '#E6F4EC',
  'green-dark': '#0F2C28',
  'blue-light': '#E6F1F8',
  'blue-dark': '#0F2438',
  'red-light': '#F6ECEA',
  'red-dark': '#2C1412',
}

/** Accent chip shown in the theme picker (stronger than page wash). */
export const THEME_SWATCH: Record<ThemeId, { bg: string; fg: string }> = {
  'green-light': { bg: '#E6F4EC', fg: '#00413E' },
  'green-dark': { bg: '#0F2C28', fg: '#7dcfb6' },
  'blue-light': { bg: '#E6F1F8', fg: '#0A3A5C' },
  'blue-dark': { bg: '#0F2438', fg: '#7db8cf' },
  'red-light': { bg: '#F6ECEA', fg: '#5C1A18' },
  'red-dark': { bg: '#2C1412', fg: '#cf8a7d' },
}

interface ThemeContextType {
  theme: ThemeId
  palette: ThemePalette
  mode: ThemeMode
  setTheme: (theme: ThemeId) => void
  toggleTheme: () => void
}

const STORAGE_KEY = 'academ-theme'
const PALETTE_CLASSES = ['palette-green', 'palette-blue', 'palette-red'] as const

const ThemeContext = createContext<ThemeContextType>({
  theme: 'green-light',
  palette: 'green',
  mode: 'light',
  setTheme: () => {},
  toggleTheme: () => {},
})

function parseThemeId(raw: string | null): ThemeId | null {
  if (!raw) return null
  if ((THEME_ORDER as string[]).includes(raw)) return raw as ThemeId
  // Legacy binary values from earlier Academ / BRC builds
  if (raw === 'dark') return 'green-dark'
  if (raw === 'light') return 'green-light'
  return null
}

function splitTheme(id: ThemeId): { palette: ThemePalette; mode: ThemeMode } {
  const [palette, mode] = id.split('-') as [ThemePalette, ThemeMode]
  return { palette, mode }
}

function readStoredTheme(): ThemeId {
  if (typeof window === 'undefined') return 'green-light'
  return (
    parseThemeId(localStorage.getItem(STORAGE_KEY)) ||
    parseThemeId(localStorage.getItem('brc-theme')) ||
    'green-light'
  )
}

function applyThemeToDocument(theme: ThemeId) {
  const { palette, mode } = splitTheme(theme)
  const root = document.documentElement
  root.classList.remove('dark', 'light', ...PALETTE_CLASSES)
  root.classList.add(mode, `palette-${palette}`)
  root.style.colorScheme = mode
  localStorage.setItem(STORAGE_KEY, theme)
  // Keep a binary value for any shared BRC readers; Academ uses academ-theme for full id.
  localStorage.setItem('brc-theme', mode)
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', THEME_COLORS[theme])
}

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeId>(() => readStoredTheme())

  useEffect(() => {
    applyThemeToDocument(theme)
  }, [theme])

  const toggleTheme = () =>
    setTheme((prev) => {
      const i = THEME_ORDER.indexOf(prev)
      return THEME_ORDER[(i + 1) % THEME_ORDER.length]
    })

  const value = useMemo(() => {
    const { palette, mode } = splitTheme(theme)
    return { theme, palette, mode, setTheme, toggleTheme }
  }, [theme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export const useTheme = () => useContext(ThemeContext)
